import { NavList } from '@/app/shell/SideBar/SideBarNavList';
import type { SideBarItem } from '@/app/shell/SideBar/sideBarItem';
import logo from '@/assets/brand/isotype.png';
import { ROUTES } from '@/shared/constants/routes';
import { AnimatePresence, motion } from 'framer-motion';
import { Home, Menu, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';

export function MobileDrawer({
  items,
  activeItem,
  onItemClick,
  isOpen,
  onClose,
  groupLabels,
  collapsibleGroups,
  openGroups,
  onToggleGroup,
  activeGroup,
  footer,
}: {
  readonly items: SideBarItem[];
  readonly activeItem?: string | undefined;
  readonly onItemClick: (item: SideBarItem) => void;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly groupLabels?: Record<string, string> | undefined;
  readonly collapsibleGroups?: readonly string[] | undefined;
  readonly openGroups?: Readonly<Record<string, boolean>> | undefined;
  readonly onToggleGroup?: ((groupName: string) => void) | undefined;
  readonly activeGroup?: string | undefined;
  readonly footer?: ReactNode;
}) {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
            onClick={onClose}
          />

          <motion.aside
            initial={{ x: '-100%' }}
            animate={{ x: 0 }}
            exit={{ x: '-100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed top-0 left-0 z-50 flex h-full w-72 flex-col border-r shadow-xl bg-surface border-border md:hidden"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="flex items-center gap-2">
                <Link
                  to={ROUTES.WORKSPACES}
                  aria-label="Go to workspaces"
                  className="flex items-center"
                >
                  <img src={logo} alt="Teramot" className="h-6 w-auto" />
                </Link>
                <Link
                  to={ROUTES.WORKSPACES}
                  aria-label="Go to workspaces"
                  className="grid size-7 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground"
                >
                  <Home size={16} />
                </Link>
              </span>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="grid size-8 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground"
              >
                <X size={20} />
              </button>
            </div>
            <NavList
              items={items}
              activeItem={activeItem}
              onItemClick={onItemClick}
              onClose={onClose}
              groupLabels={groupLabels}
              collapsibleGroups={collapsibleGroups}
              openGroups={openGroups}
              onToggleGroup={onToggleGroup}
              activeGroup={activeGroup}
              idPrefix="sidebar-mobile"
              collapsed={false}
            />
            {footer}
          </motion.aside>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
}

export function MobileMenuTrigger({ onClick }: { readonly onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open menu"
      className="grid size-8 cursor-pointer place-items-center rounded-md text-muted-foreground transition-colors hover:bg-surface-secondary hover:text-foreground md:hidden"
    >
      <Menu size={24} />
    </button>
  );
}
